import { Alert, Button, Card, Group, NumberInput, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { type Schemas, api, call, hasPermission } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { ErrorAlert, Loading, notifyError, notifyOk } from "../../components/common";

type Values = Schemas["KpiValues"];
type Key = keyof Schemas["EffectiveKpi"];

const BOOL_FIELDS: { key: Key; label: string; help: string }[] = [
  { key: "setup_is_planned", label: "Setup counts as planned time", help: "Setup/changeover is excluded from planned production time instead of reducing availability." },
  { key: "rework_counts_as_good", label: "Rework counts as good", help: "Reworked units are included in the good quantity for quality." },
  { key: "cap_performance", label: "Cap performance at 100%", help: "Values above 100% indicate a wrong ideal cycle time; they are capped and flagged." },
];
const NUM_FIELDS: { key: Key; label: string; help: string; suffix: string }[] = [
  { key: "minor_stop_threshold_s", label: "Micro-stop threshold", help: "Stops shorter than this count as performance loss, not downtime (0 = off).", suffix: " s" },
  { key: "oee_target_pct", label: "OEE target", help: "Used for colour coding.", suffix: " %" },
  { key: "availability_target_pct", label: "Availability target", help: "", suffix: " %" },
  { key: "performance_target_pct", label: "Performance target", help: "", suffix: " %" },
  { key: "quality_target_pct", label: "Quality target", help: "", suffix: " %" },
];

export function KpiSettingsPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const canManage = hasPermission(me, "masterdata:manage");
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const [scope, setScope] = useState<string>("company");
  const factoryId = scope === "company" ? null : scope;
  const settings = useQuery({
    queryKey: ["kpi-settings", scope],
    queryFn: () => call(api.GET("/api/v1/kpi-settings", { params: { query: factoryId ? { factory_id: factoryId } : {} } })),
  });
  const [values, setValues] = useState<Values>({});
  useEffect(() => {
    if (settings.data) setValues((factoryId ? settings.data.factory : settings.data.company) ?? {});
  }, [settings.data, factoryId]);
  const save = useMutation({
    mutationFn: () => call(api.PUT("/api/v1/kpi-settings", { body: { ...values, factory_id: factoryId } })),
    onSuccess: () => { notifyOk("KPI settings saved"); void qc.invalidateQueries({ queryKey: ["kpi-settings"] }); },
    onError: notifyError,
  });

  if (settings.isLoading || factories.isLoading) return <Loading />;
  if (settings.error) return <ErrorAlert error={settings.error} />;
  const eff = settings.data!.effective;
  const inherited = (k: Key) => {
    const parent = factoryId ? { ...settings.data!.defaults, ...strip(settings.data!.company) } : settings.data!.defaults;
    return parent[k];
  };
  const show = (v: unknown) => (typeof v === "boolean" ? (v ? "yes" : "no") : String(v));
  return (
    <Stack>
      <Group justify="space-between" align="end">
        <div>
          <Title order={2}>KPI settings</Title>
          <Text size="sm" c="dimmed">How OEE and related KPIs are calculated. Empty fields inherit (factory → company → product default).</Text>
        </div>
        <Select label="Applies to" w={240} value={scope} onChange={(v) => setScope(v ?? "company")}
          data={[{ value: "company", label: "Whole company" }, ...(factories.data ?? []).map((f) => ({ value: f.id, label: `Factory: ${f.name}` }))]} />
      </Group>
      <Alert variant="light" title="Calculation">
        OEE = Availability × Performance × Quality. Availability = run time / planned production time (shift time − breaks −
        planned stops). Performance = ideal cycle time × units / run time. Quality = good / (good + rejected).
        Full definitions: docs/KPI_DEFINITIONS.md.
      </Alert>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {BOOL_FIELDS.map((f) => (
          <Card key={f.key} withBorder>
            <Select label={f.label} description={f.help} disabled={!canManage} clearable
              placeholder={`inherit (${show(inherited(f.key))})`}
              data={[{ value: "true", label: "yes" }, { value: "false", label: "no" }]}
              value={values[f.key] === null || values[f.key] === undefined ? null : String(values[f.key])}
              onChange={(v) => setValues({ ...values, [f.key]: v === null ? null : v === "true" })} />
            <Text size="xs" mt={4}>In effect: <b>{show(eff[f.key])}</b></Text>
          </Card>
        ))}
        {NUM_FIELDS.map((f) => (
          <Card key={f.key} withBorder>
            <NumberInput label={f.label} description={f.help} disabled={!canManage} min={0} suffix={f.suffix}
              placeholder={`inherit (${show(inherited(f.key))})`}
              value={(values[f.key] as number | null | undefined) ?? ""}
              onChange={(v) => setValues({ ...values, [f.key]: v === "" ? null : Number(v) })} />
            <Text size="xs" mt={4}>In effect: <b>{show(eff[f.key])}{f.suffix}</b></Text>
          </Card>
        ))}
      </SimpleGrid>
      {canManage && <Button w="fit-content" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>}
    </Stack>
  );
}

function strip(v: Values): Partial<Schemas["EffectiveKpi"]> {
  return Object.fromEntries(Object.entries(v).filter(([, x]) => x !== null && x !== undefined));
}
